/**
 * useCopilot — single hook that owns the chat widget's state.
 *
 * Responsibilities
 * ----------------
 *  - Load & cache the user's recent sessions (left-drawer).
 *  - Load the active session's history on demand.
 *  - Send a message and append the assistant's reply when it lands.
 *  - Track rate-limit usage returned from `/chat` so the UI can nag at
 *    say 25/30 "almost at your hourly cap".
 *  - Delete a session.
 *
 * We don't use a global store — the widget is a self-contained
 * component tree, and keeping state local means it's trivially unmount-
 * safe when a user logs out.
 */

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import useAuthStore from '../../store/authStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useCopilot() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);

  const [sessions, setSessions] = useState([]);        // list for drawer
  const [sessionId, setSessionId] = useState(null);    // currently active
  const [messages, setMessages] = useState([]);        // history of active session
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sending, setSending] = useState(false);
  const [rateLimit, setRateLimit] = useState({ used: 0, remaining: 30, limit: 30 });
  const [error, setError] = useState('');

  // ---------- Sessions list ----------
  const fetchSessions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/ai-assistant/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(res.data?.sessions || []);
    } catch {
      // non-blocking — drawer just stays empty
    }
  }, [token]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // ---------- Open a specific session ----------
  const openSession = useCallback(async (sid) => {
    if (!sid) {
      // "New conversation" — just reset local state. Backend creates the
      // session lazily on the first user message.
      setSessionId(null);
      setMessages([]);
      setError('');
      return;
    }
    setLoadingHistory(true);
    setError('');
    try {
      const res = await axios.get(`${API}/ai-assistant/sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessionId(sid);
      setMessages(res.data?.messages || []);
    } catch {
      setError('Could not load that conversation.');
    } finally {
      setLoadingHistory(false);
    }
  }, [token]);

  // ---------- Send a message ----------
  const send = useCallback(async (message) => {
    const text = (message || '').trim();
    if (!text || sending) return;
    setSending(true);
    setError('');
    // Optimistic — append the user message immediately so the UI feels snappy.
    const now = new Date().toISOString();
    setMessages((prev) => [...prev, { role: 'user', content: text, created_at: now, _optimistic: true }]);
    try {
      const res = await axios.post(
        `${API}/ai-assistant/chat`,
        { message: text, session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { session_id, reply, tool_calls, actions, rate_limit } = res.data || {};
      if (!sessionId && session_id) setSessionId(session_id);
      if (rate_limit) setRateLimit(rate_limit);
      // Replace optimistic marker with the server-stored user turn + append assistant
      setMessages((prev) => {
        const noOptimistic = prev.filter((m) => !m._optimistic);
        return [
          ...noOptimistic,
          { role: 'user', content: text, created_at: now },
          { role: 'assistant', content: reply || '(no response)', tool_calls: tool_calls || [], actions: actions || [], created_at: new Date().toISOString() },
        ];
      });
      // Refresh sessions drawer so the new/updated session shows at top
      fetchSessions();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = 'Something went wrong — try again.';
      if (typeof detail === 'string') msg = detail;
      else if (detail?.message) msg = detail.message;
      setError(msg);
      // Drop the optimistic user turn — otherwise the UI would show a
      // ghost message that the server never persisted.
      setMessages((prev) => prev.filter((m) => !m._optimistic));
    } finally {
      setSending(false);
    }
  }, [sending, sessionId, token, fetchSessions]);

  // ---------- Confirm / cancel a staged action ----------
  const resolveAction = useCallback(async (actionId, verb) => {
    setError('');
    try {
      const res = await axios.post(`${API}/ai-assistant/actions/${actionId}/${verb}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updated = res.data;
      setMessages((prev) => {
        const next = prev.map((m) => (m.actions || []).some((a) => a.id === actionId)
          ? { ...m, actions: m.actions.map((a) => (a.id === actionId ? { ...a, ...updated } : a)) }
          : m);
        if (verb === 'confirm' && updated?.result?.summary) {
          const prefix = updated.status === 'executed' ? 'Confirmed and done: ' : 'Action failed: ';
          next.push({ role: 'assistant', content: prefix + updated.result.summary, created_at: new Date().toISOString() });
        }
        return next;
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not process that action.');
    }
  }, [token]);

  const confirmAction = useCallback((id) => resolveAction(id, 'confirm'), [resolveAction]);
  const cancelAction = useCallback((id) => resolveAction(id, 'cancel'), [resolveAction]);

  // ---------- Delete session ----------
  const removeSession = useCallback(async (sid) => {
    try {
      await axios.delete(`${API}/ai-assistant/sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (sid === sessionId) {
        setSessionId(null);
        setMessages([]);
      }
      fetchSessions();
    } catch {
      setError('Could not delete that conversation.');
    }
  }, [sessionId, token, fetchSessions]);

  return {
    user,
    sessions,
    sessionId,
    messages,
    loadingHistory,
    sending,
    rateLimit,
    error,
    openSession,
    send,
    removeSession,
    confirmAction,
    cancelAction,
    refreshSessions: fetchSessions,
  };
}
